import { useUserStore } from '@/store/user'
import { Button } from '@/components/ui/button'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { LogOut, Loader2, Headphones, BookOpen } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useConfigStore } from '@/store/config'
import { useEffect, useState } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import InsufficientBalanceModal from '@/components/InsufficientBalanceModal'
import RechargePanel from '@/components/RechargePanel'
import { INSUFFICIENT_BALANCE_CHANNEL } from '@/service/constants/system'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import { ContactQRCode } from '@/components/common/ContactQRCode'
import { navTabClass } from '@/components/layout/nav-tab-classes'
import { wsClient } from '@/lib/ws-client'
import { WSServerMessageType } from '@/types/ws'
import { cn } from '@/lib/utils'
import { dialogShell, gradientBtn } from '@/lib/ui-classes'

export function Header() {
  const { user, logout, fetchUser, exitImpersonationMode } = useUserStore()
  const router = useRouter()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isAuthChecked, setIsAuthChecked] = useState(false)
  const [exitingImpersonation, setExitingImpersonation] = useState(false)

  // Insufficient Balance & Recharge States
  const [isInsufficientBalanceOpen, setIsInsufficientBalanceOpen] = useState(false)
  const [isRechargeOpen, setIsRechargeOpen] = useState(false)

  // 监听余额不足全局通知
  useEffect(() => {
    if (!isAuthenticated || !user) return

    const handleInsufficientBalance = () => {
      setIsInsufficientBalanceOpen(true)
    }

    const unsubscribe = wsClient.on(WSServerMessageType.BALANCE_INSUFFICIENT, handleInsufficientBalance)

    return () => {
      unsubscribe()
    }
  }, [isAuthenticated, user])

  // Listen for Insufficient Balance
  useEffect(() => {
    const channel = new BroadcastChannel(INSUFFICIENT_BALANCE_CHANNEL)
    channel.onmessage = (event) => {
      if (event.data?.type === 'INSUFFICIENT_BALANCE') {
        setIsInsufficientBalanceOpen(true)
      }
    }
    return () => {
      channel.close()
    }
  }, [])

  // Check authentication status on client side
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      setIsAuthenticated(true)
    } else {
      setIsAuthenticated(false)
    }
    setIsAuthChecked(true)
  }, [user])

  const handleLogout = () => {
    logout()
    setIsAuthenticated(false)
    router.push('/auth/login')
  }

  const handleExitImpersonation = async () => {
    setExitingImpersonation(true)
    try {
      await exitImpersonationMode()
      router.push('/auth/login')
    } finally {
      setExitingImpersonation(false)
    }
  }

  // Get display name (nickname or username)
  const displayName = user?.nickname || user?.username || '用户'
  // Get avatar initial (first char of display name)
  const avatarInitial = displayName.charAt(0).toUpperCase()

  const systemConfig = useConfigStore(state => state.config)
  // Get site name from env
  const siteName = systemConfig?.site_name || ''
  const siteLogo = systemConfig?.site_logo || ''
  const isShowSiteName = systemConfig?.is_show_site_name === true

  const contactIsShow = systemConfig?.contact_is_show || false
  const virtualCurrency = systemConfig?.virtual_currency || '积分'
  const docUrl = systemConfig?.doc_url || ''

  const isHomePage = router.pathname === '/'
  const sessionContext = user?.session_context
  const isImpersonation = Boolean(sessionContext?.is_impersonation)
  const canWrite = sessionContext?.can_write ?? true

  const homeGhostBtn =
    'border border-white/10 bg-white/[0.05] text-zinc-100 hover:bg-white/[0.1] hover:text-white rounded-full backdrop-blur-xl transition-all px-6 gap-2'


  return (
    <>
    <header className="w-full">
      {isAuthenticated && user && isImpersonation && (
        <div className="border-b border-amber-500/20 bg-amber-500/10 px-4 py-2">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 text-xs text-amber-100 sm:text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">当前为管理员代登录模式</span>
              <span className="text-amber-200/80">
                {canWrite ? '当前会话允许修改数据' : '当前会话为只读模式，不支持修改数据'}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExitImpersonation}
              disabled={exitingImpersonation}
              className="h-7 border-amber-500/30 bg-amber-500/10 text-amber-50 hover:bg-amber-500/20 hover:text-white"
            >
              {exitingImpersonation ? '退出中...' : '退出代登录'}
            </Button>
          </div>
        </div>
      )}
      <div className="h-16 flex items-center justify-between px-4 sm:px-6">
        {/* Left: Logo & Site Name */}
        <Link href="/" className="flex items-center gap-3 min-w-0 sm:min-w-[200px]">
          {siteLogo && <img src={siteLogo} alt="Logo" className="h-10 w-auto max-h-10 object-contain" />}
          {isShowSiteName && siteName && (
            <span className="font-bold text-md hidden sm:block">{siteName}</span>
          )}
        </Link>

        {/* Center: Menu Tabs - Hide on Home Page */}
        <div className="flex-1 flex justify-center min-w-0">
          {!isHomePage && (
            <Tabs
              value={
                router.pathname.startsWith('/prompts') ? 'prompts'
                : router.pathname.startsWith('/orders') ? 'orders'
                : router.pathname.startsWith('/usage') ? 'usage'
                : router.pathname.startsWith('/settings') ? 'settings'
                : 'project'
              }
              className="w-full sm:w-auto overflow-x-auto scrollbar-hide"
              onValueChange={(value) => {
                if (value === 'orders') router.push('/orders')
                if (value === 'usage') router.push('/usage')
                if (value === 'prompts') router.push('/prompts')
                if (value === 'settings') router.push('/settings')
              }}
            >
              <TabsList className="bg-transparent w-max sm:w-auto px-2 sm:px-1 mx-auto gap-1 sm:gap-1.5 h-auto p-0">
                <TabsTrigger 
                  value="project" 
                  className={navTabClass('data-[state=active]:shadow-none text-xs sm:text-sm px-3 sm:px-4 py-2')}
                  onClick={() => router.push('/projects')}
                >
                  项目管理
                </TabsTrigger>
                <TabsTrigger value="orders" className={navTabClass('data-[state=active]:shadow-none text-xs sm:text-sm px-3 sm:px-4 py-2')}>
                  订单管理
                </TabsTrigger>
                <TabsTrigger value="usage" className={navTabClass('data-[state=active]:shadow-none text-xs sm:text-sm px-3 sm:px-4 py-2')}>
                  使用记录
                </TabsTrigger>
                <TabsTrigger value="prompts" className={navTabClass('data-[state=active]:shadow-none text-xs sm:text-sm px-3 sm:px-4 py-2')}>
                  提示词管理
                </TabsTrigger>
                <TabsTrigger value="settings" className={navTabClass('data-[state=active]:shadow-none text-xs sm:text-sm px-3 sm:px-4 py-2')}>
                  个人中心
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>

        {/* Right: User Info or Login Button */}
        <div className="flex items-center justify-end gap-2 sm:gap-4 min-w-0 sm:min-w-[200px]">
          {!isAuthChecked ? (
            <div className="w-8 h-8 flex items-center justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
            </div>
          ) : isAuthenticated && user && !isHomePage ? (
            <div className="flex items-center gap-3">
              {docUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-zinc-400 hover:text-white gap-2 hidden sm:flex"
                  onClick={() => window.open(docUrl, '_blank')}
                >
                  <BookOpen className="h-3 w-3" />
                  教程文档
                </Button>
              )}

              {contactIsShow && <HoverCard openDelay={200} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-xs text-zinc-400 hover:text-white gap-2 hidden sm:flex">
                    <Headphones className="h-3 w-3" />
                    联系客服
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent className="w-auto p-4 bg-zinc-950 border-zinc-800" align="end">
                  <div className="flex flex-col items-center gap-2">
                    <ContactQRCode size={160} />
                    <span className="text-sm text-zinc-400 max-w-[160px] text-center">技术支持，请微信扫码联系</span>
                  </div>
                </HoverCardContent>
              </HoverCard>}

              <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.03] border border-white/10 rounded-full mr-2">
                <div className="w-2 h-2 rounded-full bg-[#07F4F2] animate-pulse" />
                <span className="text-xs text-zinc-400">{virtualCurrency}</span>
                <span className="text-sm font-semibold text-zinc-200 tabular-nums">
                  {user?.balance || '0'}
                </span>
                <div className="w-[1px] h-3 bg-white/10 mx-1.5" />
                <span
                  className="text-xs font-medium text-zinc-400 hover:text-[#07F4F2] cursor-pointer transition-colors"
                  onClick={() => setIsRechargeOpen(true)}
                >
                  充值
                </span>
              </div>

              {(user?.is_promoter || (Number(user?.cash_balance) || 0) > 0) && (
                <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-full mr-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs text-zinc-400">现金余额</span>
                  <span className="text-sm font-semibold text-emerald-300 tabular-nums">
                    ¥{((Number(user?.cash_balance) || 0)).toFixed(2)}
                  </span>
                  <div className="w-[1px] h-3 bg-white/10 mx-1.5" />
                  <span
                    className="text-xs font-medium text-zinc-400 hover:text-emerald-300 cursor-pointer transition-colors"
                    onClick={() => router.push('/settings')}
                  >
                    明细
                  </span>
                </div>
              )}

              <Link href="/settings">
                <div className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity">
                  <span className="text-sm font-medium text-zinc-200 hidden sm:block">
                    {displayName}
                  </span>
                  <Avatar className="h-8 w-8 border border-white/10 hover:border-[#07F4F2]/40 transition-colors">
                    <AvatarImage src={user.avatar_url || user.avatar || ''} alt={displayName} />
                    <AvatarFallback className="bg-black/35 text-zinc-200">
                      {avatarInitial}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-full h-8 w-8"
                title="退出登录"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {docUrl && (
                <Link href={docUrl} target="_blank">
                  <Button
                    size="sm"
                    className={isHomePage ? homeGhostBtn : 'border border-zinc-500 bg-black/30 hover:bg-black/50 text-white hover:text-white rounded-full backdrop-blur-sm transition-all px-6 gap-2'}
                  >
                    <BookOpen className="h-4 w-4" />
                    文档
                  </Button>
                </Link>
              )}
              {isAuthenticated ? (
                // Only show Enter Console if we are on Home Page
                // If we are NOT on Home Page and have no user (but authenticated), it means loading user
                isHomePage ? (
                  <Link href="/projects">
                    <Button
                      size="sm"
                      className={`${gradientBtn} h-9 px-6 text-sm`}
                    >
                      进入工作台
                    </Button>
                  </Link>
                ) : (
                  <div className="w-8 h-8 flex items-center justify-center">
                    <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
                  </div>
                )
              ) : (
                <Link href="/auth/login">
                  <Button
                    size="sm"
                    className={isHomePage ? `${gradientBtn} h-9 px-6 text-sm` : 'border border-zinc-500 bg-black/30 hover:bg-black/50 text-white hover:text-white rounded-full backdrop-blur-sm transition-all px-6'}
                  >
                    登录
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Insufficient Balance Modal */}
        <InsufficientBalanceModal
          open={isInsufficientBalanceOpen}
          onOpenChange={setIsInsufficientBalanceOpen}
          onRecharge={() => setIsRechargeOpen(true)}
        />

        {/* Recharge Panel Dialog */}
        <Dialog open={isRechargeOpen} onOpenChange={setIsRechargeOpen}>
          <DialogContent className={cn(dialogShell, 'sm:max-w-[600px] overflow-hidden p-0')} onInteractOutside={(e) => e.preventDefault()}>
            <RechargePanel onClose={() => {
              setIsRechargeOpen(false)
              fetchUser()
            }} />
          </DialogContent>
        </Dialog>
      </div>
    </header>
    </>
  )
}
